import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeSlash, Lock, ArrowsClockwise, CheckCircle, XCircle } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "/dolphin-leadflow-logo.png";
const BG_URL = "https://customer-assets.emergentagent.com/job_lead-verifier/artifacts/i05v5o5k_Firefly.jpg";

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [tokenEmail, setTokenEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setTokenError("Missing reset token. Please request a new password reset link.");
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/reset-password/verify`, { params: { token } });
        setTokenValid(true);
        setTokenEmail(res.data?.email || "");
      } catch (err) {
        setTokenError(err.response?.data?.detail || "Reset link is invalid or expired.");
      } finally {
        setVerifying(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${BG_URL})` }}
    >
      <div className="absolute inset-0 bg-black/60" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="bg-white/90 backdrop-blur-xl border border-slate-200 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-6">
            <img src={LOGO_URL} alt="Dolphin LeadFlow" className="h-24 mx-auto mb-4" />
          </div>

          {verifying && (
            <div className="text-center py-8" data-testid="reset-password-verifying">
              <ArrowsClockwise size={32} className="animate-spin mx-auto text-[#006994]" />
              <p className="text-slate-500 mt-4">Verifying your reset link...</p>
            </div>
          )}

          {!verifying && !tokenValid && (
            <div className="text-center py-4" data-testid="reset-password-invalid">
              <XCircle size={48} className="mx-auto text-red-400 mb-4" />
              <h1 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Link expired
              </h1>
              <p className="text-slate-500 mb-6">{tokenError}</p>
              <Button
                onClick={() => navigate("/login")}
                className="bg-[#006994] hover:bg-[#005A80] text-white font-bold"
                data-testid="reset-back-to-login-btn"
              >
                Back to sign in
              </Button>
            </div>
          )}

          {!verifying && tokenValid && !done && (
            <>
              <h1 className="text-3xl font-black text-slate-900 text-center mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Set a new password
              </h1>
              <p className="text-slate-500 text-center mb-6 text-sm">
                {tokenEmail ? <>For <span className="font-mono text-slate-900">{tokenEmail}</span></> : "Enter and confirm a new password."}
              </p>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-slate-500 text-sm">New password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min 8 characters"
                      className="pl-10 pr-10 bg-slate-100 border-slate-200 focus:border-[#006994] text-slate-900 h-12 rounded-lg"
                      data-testid="reset-new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                    >
                      {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-500 text-sm">Confirm password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Re-enter your new password"
                      className="pl-10 bg-slate-100 border-slate-200 focus:border-[#006994] text-slate-900 h-12 rounded-lg"
                      data-testid="reset-confirm-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={submitting || !password || !confirm}
                  className="w-full bg-[#006994] hover:bg-[#005A80] text-white font-bold h-12 rounded-lg  mt-6"
                  data-testid="reset-submit-btn"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2"><ArrowsClockwise size={16} className="animate-spin" /> Updating...</span>
                  ) : (
                    "Update password"
                  )}
                </Button>
              </form>
            </>
          )}

          {done && (
            <div className="text-center py-4" data-testid="reset-password-success">
              <CheckCircle size={48} className="mx-auto text-emerald-400 mb-4" />
              <h1 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Password updated
              </h1>
              <p className="text-slate-500 mb-6">You can now sign in with your new password.</p>
              <Button
                onClick={() => navigate("/login")}
                className="bg-[#006994] hover:bg-[#005A80] text-white font-bold"
                data-testid="reset-go-to-login-btn"
              >
                Go to sign in
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
